<template>
  <base-material-card
    icon="mdi-bell-alert"
    title="Notice Types"
    class="px-5 py-3"
  >
    <v-btn
      color="green"
      dark
      large
      absolute
      top
      right
      fab
      :disabled="isNewNoticeRowOpen"
      @click="addNewNoticeRow"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>

    <v-data-table
      :headers="headers"
      :items="noticeTypes"
      item-key="id"
    >
      <!-- Top toolbar template -->
      <!-- <template v-slot:top>
        <v-toolbar flat>
          <v-btn
            color="primary"
            dark
            :disabled="isNewNoticeRowOpen"
            @click="addNewNoticeRow"
          >
            Add Notice Type
          </v-btn>
        </v-toolbar>
      </template> -->

      <!-- Name column -->
      <template v-slot:item.name="{ item }">
        <template v-if="editedNotice.id === item.id">
          <v-text-field
            v-model="editedNotice.name"
            dense
            hide-details
            placeholder="Name"
          />
        </template>
        <template v-else>
          {{ item.name }}
        </template>
      </template>

      <!-- Level column -->
      <template v-slot:item.level="{ item }">
        <template v-if="editedNotice.id === item.id">
          <v-text-field
            v-model.number="editedNotice.level"
            type="number"
            dense
            hide-details
            placeholder="Level"
          />
        </template>
        <template v-else>
          {{ item.level }}
        </template>
      </template>

      <!-- Expiration months column -->
      <template v-slot:item.expirationMonths="{ item }">
        <template v-if="editedNotice.id === item.id">
          <v-text-field
            v-model.number="editedNotice.expirationMonths"
            type="number"
            dense
            hide-details
            placeholder="Expiration Months"
          />
        </template>
        <template v-else>
          {{ item.expirationMonths }}
        </template>
      </template>

      <!-- Description column -->
      <template v-slot:item.description="{ item }">
        <template v-if="editedNotice.id === item.id">
          <v-text-field
            v-model="editedNotice.description"
            dense
            hide-details
            placeholder="Description"
          />
        </template>
        <template v-else>
          {{ item.description }}
        </template>
      </template>

      <!-- Actions column -->
      <template v-slot:item.actions="{ item }">
        <template v-if="editedNotice.id === item.id">
          <v-icon
            small
            class="mr-2"
            @click="saveNoticeType"
          >
            mdi-check
          </v-icon>
          <v-icon
            small
            @click="cancelNoticeEdit"
          >
            mdi-close
          </v-icon>
        </template>
        <template v-else>
          <v-icon
            small
            class="mr-2"
            @click="editNoticeType(item)"
          >
            mdi-pencil
          </v-icon>
          <v-icon
            small
            @click="deleteNoticeType(item)"
          >
            mdi-delete
          </v-icon>
          <v-icon
            small
            class="ml-2"
            @click="openTemplateEditor(item)"
          >
            mdi-file-document-edit
          </v-icon>
        </template>
      </template>
    </v-data-table>

    <!-- Template Editor Dialog -->
    <v-dialog
      v-model="templateDialog"
      max-width="800px"
    >
      <v-card>
        <v-card-title>
          <span class="headline">Edit Template</span>
        </v-card-title>

        <v-card-text>
          <v-container>
            <tiptap-vuetify
              v-model="editedTemplate"
              :extensions="extensions"
              :toolbar-attributes="{ color: 'black' }"
              :card-props="{ flat: true }"
              placeholder="Write your template content..."
            />
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey darken-1"
            text
            @click="closeTemplateEditor"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            @click="saveTemplate"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </base-material-card>
</template>

<script>
  import {
    Q_GET_NOTICE_TYPES,
    M_CREATE_NOTICE_TYPE,
    M_UPDATE_NOTICE_TYPE,
    M_DELETE_NOTICE_TYPE,
  } from '@/gql/noticeType'
  import { TiptapVuetify, Heading, Bold, Italic, Strike, Underline, Code, Paragraph, BulletList, OrderedList, ListItem, Link, Blockquote, HardBreak, HorizontalRule, History } from 'tiptap-vuetify'

  export default {
    name: 'NoticeTypes',
    components: { TiptapVuetify },

    data: () => ({
      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Level', value: 'level' },
        { text: 'Expiration (Months)', value: 'expirationMonths' },
        { text: 'Description', value: 'description' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      noticeTypes: [],
      editedNotice: {
        id: null,
        name: '',
        level: null,
        expirationMonths: null,
        description: '',
        template: '',
      },
      defaultNotice: {
        id: null,
        name: '',
        level: null,
        expirationMonths: null,
        description: '',
        template: '',
      },
      templateDialog: false,
      editedTemplate: '',
      extensions: [
        History,
        Blockquote,
        Link,
        Underline,
        Strike,
        Italic,
        ListItem,
        BulletList,
        OrderedList,
        [Heading, {
          options: {
            levels: [1, 2, 3]
          }
        }],
        Bold,
        Code,
        HorizontalRule,
        Paragraph,
        HardBreak
      ]
    }),

    computed: {
      isNewNoticeRowOpen () {
        return this.editedNotice.id === 0
      },
    },

    created () {
      this.fetchData()
    },

    methods: {
      async fetchData () {
        try {
          const { data } = await this.$apollo.query({
            query: Q_GET_NOTICE_TYPES,
          })
          this.noticeTypes = data.noticeTypes
        } catch (error) {
          console.error('Error fetching notice types:', error)
        }
      },

      addNewNoticeRow () {
        this.editedNotice = { ...this.defaultNotice, id: 0 }
        this.noticeTypes.unshift(this.editedNotice)
      },

      editNoticeType (item) {
        this.editedNotice = { ...item }
      },

      async saveNoticeType () {
        try {
          if (this.editedNotice.id === 0) {
            const { data } = await this.$apollo.mutate({
              mutation: M_CREATE_NOTICE_TYPE,
              variables: {
                name: this.editedNotice.name,
                level: this.editedNotice.level,
                expirationMonths: this.editedNotice.expirationMonths,
                description: this.editedNotice.description,
              },
            })
            this.noticeTypes = this.noticeTypes.filter(item => item.id !== 0)
            this.noticeTypes.push(data.createNoticeType)
          } else {
            const { data } = await this.$apollo.mutate({
              mutation: M_UPDATE_NOTICE_TYPE,
              variables: {
                id: this.editedNotice.id,
                name: this.editedNotice.name,
                level: this.editedNotice.level,
                expirationMonths: this.editedNotice.expirationMonths,
                description: this.editedNotice.description,
              },
            })
            const index = this.noticeTypes.findIndex(item => item.id === this.editedNotice.id)
            this.noticeTypes.splice(index, 1, data.updateNoticeType)
          }
          this.editedNotice = { ...this.defaultNotice }
        } catch (error) {
          console.error('Error saving notice type:', error)
        }
      },

      cancelNoticeEdit () {
        if (this.editedNotice.id === 0) {
          this.noticeTypes = this.noticeTypes.filter(item => item.id !== 0)
        }
        this.editedNotice = { ...this.defaultNotice }
      },

      async deleteNoticeType (item) {
        if (confirm('Are you sure you want to delete this notice type?')) {
          try {
            await this.$apollo.mutate({
              mutation: M_DELETE_NOTICE_TYPE,
              variables: {
                where: { id: item.id },
              },
            })
            this.noticeTypes = this.noticeTypes.filter(type => type.id !== item.id)
          } catch (error) {
            console.error('Error deleting notice type:', error)
          }
        }
      },

      openTemplateEditor (item) {
        this.editedNotice = { ...item }
        this.editedTemplate = item.template || ''
        this.templateDialog = true
      },

      closeTemplateEditor () {
        this.templateDialog = false
        this.editedTemplate = ''
        this.editedNotice = { ...this.defaultNotice }
      },

      async saveTemplate () {
        try {
          const { data } = await this.$apollo.mutate({
            mutation: M_UPDATE_NOTICE_TYPE,
            variables: {
              id: this.editedNotice.id,
              template: this.editedTemplate,
            },
          })
          
          const index = this.noticeTypes.findIndex(item => item.id === this.editedNotice.id)
          this.noticeTypes.splice(index, 1, data.updateNoticeType)
          
          this.closeTemplateEditor()
          this.$toast.success('Template updated successfully')
        } catch (error) {
          console.error('Error saving template:', error)
          this.$toast.error('Error updating template')
        }
      },
    },
  }
</script>
